import { z } from "zod";
import {
  airportTimezone,
  flightDirectionSchema,
  transportModeSchema,
} from "@event-planner/shared";

/**
 * Admin form schema for travel items (ADR-120).
 *
 * Per-kind detail schemas are redefined here (rather than reused from
 * `travelItemDetailsSchema`) because the form holds **strings** for
 * date / datetime fields, while the shared schema holds `TimestampLike`
 * (Firestore's persisted shape). The form's submit handler converts
 * strings → Timestamps via `dateTimeLocalToTimestamp` at the service
 * boundary, mirroring SessionForm.
 *
 * Audience model: `pickedAudienceIds[]` replaces the prior split between
 * `audienceGroupIds[]` + `audienceUserIds[]`. The form's audience picker
 * emits the IDs the picker resolved to (named audiences directly, plus
 * personal-audience IDs for picked emails); the service either reuses a
 * single audience or composes an anonymous audience around them via
 * Firestore writeBatch.
 *
 * Locations: kind-aware named slots `pickupLocationId` /
 * `dropoffLocationId`.
 */

const flightDetailsFormSchema = z.object({
  kind: z.literal('flight'),
  direction: flightDirectionSchema.nullable(),
  airline: z.string().nullable(),
  flightNumber: z.string().nullable(),
  departureAirport: z.string().nullable(),
  departureAt: z.string().nullable(),
  departureTerminal: z.string().nullable(),
  arrivalAirport: z.string().nullable(),
  arrivalAt: z.string().nullable(),
  arrivalTerminal: z.string().nullable(),
  confirmationCode: z.string().nullable(),
  notes: z.string().nullable(),
});

const lodgingDetailsFormSchema = z.object({
  kind: z.literal('lodging'),
  checkInAt: z.string().nullable(),
  checkOutAt: z.string().nullable(),
  confirmationCode: z.string().nullable(),
  notes: z.string().nullable(),
});

const transportDetailsFormSchema = z.object({
  kind: z.literal('transport'),
  mode: transportModeSchema,
  /** Null for open-ended vouchers (e.g. Uber code valid throughout event). */
  pickupAt: z.string().nullable(),
  code: z.string().nullable(),
  voucherUrl: z
    .string()
    .url('Enter a valid voucher link')
    .or(z.literal(''))
    .nullable(),
  /** Optional spend cap. Both fields together or both null. */
  spendLimitAmount: z.number().nullable(),
  spendLimitCurrency: z.string().nullable(),
  /** Optional validity window — form-side strings, converted at submit. */
  validityFrom: z.string().nullable(),
  validityUntil: z.string().nullable(),
  notes: z.string().nullable(),
});

const otherDetailsFormSchema = z.object({
  kind: z.literal('other'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().nullable(),
  time: z.string().nullable(),
  notes: z.string().nullable(),
});

export const travelDetailsFormSchema = z.discriminatedUnion('kind', [
  flightDetailsFormSchema,
  lodgingDetailsFormSchema,
  transportDetailsFormSchema,
  otherDetailsFormSchema,
]);

// Form-side reminder: always present (never null) so SchemaField can drive
// the toggle directly. The form's submit handler collapses to null on
// disabled before persisting, so the doc shape (`reminder: TravelReminder | null`)
// is unchanged.
export const travelReminderFormSchema = z.object({
  enabled: z.boolean(),
  leadMinutes: z.number().int().nonnegative(),
  body: z.string(),
});

export const travelFormSchema = z
  .object({
    details: travelDetailsFormSchema,
    /** Pickup location (transport kind only). Null otherwise. */
    pickupLocationId: z.string().nullable(),
    /**
     * Dropoff location for transport, or the single map location for
     * lodging / other. Null for flights and unset.
     */
    dropoffLocationId: z.string().nullable(),
    pickedAudienceIds: z.array(z.string()).min(1, "Pick at least one audience"),
    sessionLink: z.string().nullable(),
    reminder: travelReminderFormSchema,
  })
  .superRefine((data, ctx) => {
    if (data.details.kind === 'flight') {
      const details = data.details;
      const departureAirport = details.departureAirport?.trim().toUpperCase();
      const arrivalAirport = details.arrivalAirport?.trim().toUpperCase();
      if (details.departureAt && !departureAirport) {
        ctx.addIssue({
          code: 'custom',
          path: ['details', 'departureAirport'],
          message: 'Departure airport is required when take off time is set',
        });
      } else if (departureAirport && !airportTimezone(departureAirport)) {
        ctx.addIssue({
          code: 'custom',
          path: ['details', 'departureAirport'],
          message: `Unsupported airport timezone for ${departureAirport}`,
        });
      }
      if (details.arrivalAt && !arrivalAirport) {
        ctx.addIssue({
          code: 'custom',
          path: ['details', 'arrivalAirport'],
          message: 'Arrival airport is required when landing time is set',
        });
      } else if (arrivalAirport && !airportTimezone(arrivalAirport)) {
        ctx.addIssue({
          code: 'custom',
          path: ['details', 'arrivalAirport'],
          message: `Unsupported airport timezone for ${arrivalAirport}`,
        });
      }
    }
    if (data.reminder.enabled) {
      if (data.reminder.leadMinutes <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['reminder', 'leadMinutes'],
          message: 'Lead time must be greater than zero when reminder is on',
        });
      }
      if (!data.reminder.body || data.reminder.body.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['reminder', 'body'],
          message: 'Body is required when reminder is on',
        });
      }
    }
  });

export type TravelFormData = z.infer<typeof travelFormSchema>;
export type TravelDetailsFormData = z.infer<typeof travelDetailsFormSchema>;
